/** @jsxImportSource ../i18n */
import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CheckCircle2, Lock, Mail } from 'lucide-react';
import api from '../services/api';
import { AuthLayout } from '../components/shell/AuthLayout';
import { Banner, Button, Field, IconTile, Input } from '../components/ui';

const BackToLogin = ({ className }) => (
  <Button as={Link} to="/login" variant="secondary" leadingIcon={ArrowLeft} className={className}>
    Retour à la connexion
  </Button>
);

// RequestLink asks for the reset email; the server answers the same way
// whether the address has an account or not.
const RequestLink = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await api.post('/auth/reset-password', { email });
      setSuccess(true);
    } catch (err) {
      setError('Impossible d’envoyer l’email de réinitialisation. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <AuthLayout>
        <IconTile icon={CheckCircle2} tone="gain" size="lg" />
        <h2 className="mt-6 font-display text-display-md font-normal text-fg">Vérifiez votre boîte mail</h2>
        <p className="mt-3 text-[15px] leading-relaxed text-fg-muted">
          Si un compte existe pour <span className="font-medium text-fg">{email}</span>, un lien de réinitialisation valable une heure vient d’y être envoyé.
        </p>
        <BackToLogin className="mt-8" />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <h2 className="font-display text-display-md font-normal text-fg">Réinitialiser le mot de passe</h2>
      <p className="mt-2 text-[15px] text-fg-muted">Saisissez votre adresse email pour recevoir un lien de réinitialisation.</p>

      {error && <Banner tone="loss" role="alert" className="mt-6">{error}</Banner>}

      <form onSubmit={handleSubmit} className="mt-8 space-y-5">
        <Field label="Adresse email" htmlFor="reset-email">
          <Input
            id="reset-email"
            type="email"
            autoComplete="email"
            leadingIcon={Mail}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="vous@exemple.fr"
            required
          />
        </Field>
        <Button type="submit" size="lg" fullWidth loading={loading} trailingIcon={ArrowRight}>
          Envoyer le lien
        </Button>
      </form>

      <p className="mt-8 text-center text-sm">
        <Link to="/login" className="inline-flex items-center gap-1.5 font-medium text-gold-300 transition-colors hover:text-gold-200">
          <ArrowLeft size={15} aria-hidden="true" />
          Retour à la connexion
        </Link>
      </p>
    </AuthLayout>
  );
};

// ChooseNewPassword spends the emailed token (?token=).
const ChooseNewPassword = ({ token }) => {
  const [form, setForm] = useState({ password: '', confirm: '' });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères.');
      return;
    }
    if (form.password !== form.confirm) {
      setError('Les mots de passe ne correspondent pas.');
      return;
    }
    setLoading(true);
    try {
      await api.post('/auth/reset-password/confirm', { token, password: form.password });
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.error || 'Ce lien n’est plus valable : demandez-en un nouveau.');
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <AuthLayout>
        <IconTile icon={CheckCircle2} tone="gain" size="lg" />
        <h2 className="mt-6 font-display text-display-md font-normal text-fg">Mot de passe modifié</h2>
        <p className="mt-3 text-[15px] leading-relaxed text-fg-muted">Vous pouvez vous connecter avec votre nouveau mot de passe.</p>
        <BackToLogin className="mt-8" />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <h2 className="font-display text-display-md font-normal text-fg">Nouveau mot de passe</h2>
      <p className="mt-2 text-[15px] text-fg-muted">Choisissez un mot de passe d’au moins 8 caractères.</p>

      {error && (
        <Banner tone="loss" role="alert" className="mt-6">
          {error}{' '}
          <Link to="/reset-password" className="font-medium underline">Demander un nouveau lien</Link>
        </Banner>
      )}

      <form onSubmit={handleSubmit} className="mt-8 space-y-5">
        <Field label="Nouveau mot de passe" htmlFor="reset-password">
          <Input id="reset-password" type="password" autoComplete="new-password" leadingIcon={Lock} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required />
        </Field>
        <Field label="Confirmer le mot de passe" htmlFor="reset-confirm">
          <Input id="reset-confirm" type="password" autoComplete="new-password" leadingIcon={Lock} value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} required />
        </Field>
        <Button type="submit" size="lg" fullWidth loading={loading} trailingIcon={ArrowRight}>
          Enregistrer le mot de passe
        </Button>
      </form>
    </AuthLayout>
  );
};

const ResetPassword = () => {
  const [params] = useSearchParams();
  const token = params.get('token');
  return token ? <ChooseNewPassword token={token} /> : <RequestLink />;
};

export default ResetPassword;
